import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { makePrioStyles } from '../../../../../../theme/utils';
import { Empty, PrioSpinner, Select } from '@prio365/prio365-react-library';
import { PromanReport, PromanDocument } from '../../../../api/promanDtos';
import {
  apiGetPromanDocumentBlob,
  apiGetPromanReportMail,
} from '../../../../api/proman';
import DraggableElement from '../../../../../../dnd/components/DraggableElement';
import {
  apiCreateMessageDraft,
  apiAttachmentUpload,
  apiUpdateMessageDraft,
} from '../../../../../mail/api';
import { RcFile } from 'antd/lib/upload/interface';
import {
  DND_TYPE_PROMAN_REPORT,
  DndPromanReportDto,
} from '../../../../../../dnd/types';
import { useSelector } from 'react-redux';
import { getActiveProject } from '../../../../../../apps/main/rootReducer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = makePrioStyles((theme) => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  actionBar: {
    marginBottom: theme.old.spacing.unit(2),
  },
  documentSelector: {
    marginBottom: theme.old.spacing.unit(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.old.spacing.unit(2),
    backgroundColor: theme.colors.application.background.default,
    padding: theme.spacing.regular,
  },
  dragIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    cursor: 'grab',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: theme.colors.application.typography.active,
    },
  },
  draggableArea: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.small,
    padding: theme.spacing.small,
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    cursor: 'grab',
    transition: 'all 0.2s ease',
    backgroundColor: theme.colors.application.background.default,
    '&:hover': {
      borderColor: theme.colors.base.blue[70],
      backgroundColor: '#f5f9ff',
    },
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragIconElement: {
    fontSize: '16px',
    color: theme.colors.base.blue[70],
  },
  emailIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '18px',
    color: theme.colors.base.blue[70],
    '&:hover': {
      color: theme.colors.application.typography.active,
      transform: 'scale(1.1)',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },

  pdfViewer: {
    height: '100%',
    width: '100%',
    margin: '0 auto',
    backgroundColor: theme.colors.application.background.default,
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
  },
  loadingDocument: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%r',
  },
  emptyState: {
    padding: theme.old.spacing.unit(4),
    textAlign: 'center',
  },
}));

interface PromanReportViewerProps {
  report: PromanReport;
  documents: PromanDocument[];
  mandant?: string;
  promanProjectId: number;
}

const PromanReportViewer: React.FC<PromanReportViewerProps> = ({
  report,
  documents,
  mandant,
  promanProjectId,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null
  );
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState<boolean>(false);
  const [isCreatingEmail, setIsCreatingEmail] = useState<boolean>(false);

  const activeProjectId = useSelector(getActiveProject);

  const sortedDocuments = [...documents].sort(
    (a, b) =>
      new Date(b.lastModifiedDate).getTime() -
      new Date(a.lastModifiedDate).getTime()
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    setSelectedDocumentId(null);
    setPdfBlobUrl(null);
  }, [report.reportId]);

  useEffect(() => {
    setSelectedDocumentId(null);
    setPdfBlobUrl(null);
  }, [documents]);

  const hasDocuments = sortedDocuments.length > 0;

  const documentOptions = sortedDocuments.map((doc) => ({
    value: doc.id,
    label: doc.description || doc.fileName || `Document ${doc.id}`,
  }));

  const loadPdfBlob = async (documentId: number) => {
    setIsLoadingPdf(true);
    try {
      const response = await apiGetPromanDocumentBlob(documentId);
      const blob = new Blob([await response.arrayBuffer()], {
        type: 'application/pdf',
      });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
    } catch (error) {
      console.error('Error loading PDF:', error);
      // Fallback to original downloadUrl if available
      const selectedDoc = sortedDocuments.find((doc) => doc.id === documentId);
      if (selectedDoc?.downloadUrl) {
        setPdfBlobUrl(selectedDoc.downloadUrl);
      }
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleDocumentChange = (value: number) => {
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }

    setSelectedDocumentId(value);

    loadPdfBlob(value);
  };

  const createDragData = (): DndPromanReportDto | null => {
    if (!selectedDocumentId || !pdfBlobUrl || !activeProjectId) return null;

    const selectedDoc = sortedDocuments.find(
      (doc) => doc.id === selectedDocumentId
    );
    if (!selectedDoc) return null;

    return {
      documentId: selectedDocumentId,
      fileName:
        selectedDoc.fileName ||
        selectedDoc.description ||
        `Report_${selectedDocumentId}.pdf`,
      blobUrl: pdfBlobUrl,
      projectId: activeProjectId,
      promanProjectId,
      mandant,
    };
  };

  const handleCreateEmail = async () => {
    if (!activeProjectId || isCreatingEmail) return;

    setIsCreatingEmail(true);
    try {
      const reportMailResponse = await apiGetPromanReportMail(
        promanProjectId,
        report.reportCollectionID,
        report.reportId
      );

      if (!reportMailResponse.data) {
        console.error('Failed to fetch report mail data');
        return;
      }

      const reportMailData = reportMailResponse.data;

      const draftResponse = await apiCreateMessageDraft(activeProjectId, {
        body: {
          contentType: 1,
          content: reportMailData.body || '<p></p>',
        },
      });

      if (!draftResponse.data) {
        console.error('Failed to create draft message');
        return;
      }

      const messageId = draftResponse.data.id;

      await apiUpdateMessageDraft(
        activeProjectId,
        messageId,
        {
          subject: reportMailData.subject || '',
          toRecipients:
            reportMailData.recipients?.to?.map((emailAddress: string) => ({
              emailAddress: { address: emailAddress, name: emailAddress },
            })) || [],
          ccRecipients:
            reportMailData.recipients?.cc?.map((emailAddress: string) => ({
              emailAddress: { address: emailAddress, name: emailAddress },
            })) || [],
          bccRecipients:
            reportMailData.recipients?.bcc?.map((emailAddress: string) => ({
              emailAddress: { address: emailAddress, name: emailAddress },
            })) || [],
          body: {
            contentType: 1,
            content: reportMailData.body || '<p></p>',
          },
        },
        false
      );

      try {
        if (pdfBlobUrl === null) {
          console.error('No PDF blob URL available for attachment');
        }
        const document = await fetch(pdfBlobUrl);
        const documentBlob = await document.blob();

        const file = new File(
          [documentBlob],
          `${reportMailData.attachments[0]?.filename ?? 'Report'}.pdf`,
          {
            type: 'application/pdf',
          }
        ) as RcFile;

        await apiAttachmentUpload(activeProjectId, messageId, file);
      } catch (error) {
        console.error('Failed to upload attachments:', error);
      }

      const width = window.screen.availWidth / 2;
      const height = window.screen.availHeight / 2;
      window.open(
        `/view/${activeProjectId}/message/${messageId}/details`,
        '_blank',
        `width=${width},height=${height},noopener,noreferrer`
      );
    } catch (error) {
      console.error('Error creating email:', error);
    } finally {
      setIsCreatingEmail(false);
    }
  };

  //#endregion

  return (
    <div className={classes.container}>
      {/* ActionBar moved to PromanPage to allow opening webclient even when no report selected */}

      {hasDocuments && sortedDocuments.length >= 1 && (
        <div className={classes.documentSelector}>
          <span>{t('projects:addIn.extensions.proman.selectDocument')}</span>
          <Select
            style={{ minWidth: 200 }}
            value={selectedDocumentId}
            onChange={handleDocumentChange}
            options={documentOptions}
            placeholder={t(
              'projects:addIn.extensions.proman.selectDocumentPlaceholder'
            )}
            showSearch={false}
          />

          {selectedDocumentId && pdfBlobUrl && (
            <>
              <div
                className={classes.emailIconContainer}
                onClick={handleCreateEmail}
                title={t('projects:addIn.extensions.proman.createEmail')}
                style={{
                  opacity: isCreatingEmail ? 0.5 : 1,
                  pointerEvents: isCreatingEmail ? 'none' : 'auto',
                }}
              >
                {isCreatingEmail ? (
                  <PrioSpinner size="small" />
                ) : (
                  <FontAwesomeIcon icon={['fal', 'envelope']} />
                )}
              </div>
              <DraggableElement
                id={`proman-report-${selectedDocumentId}`}
                dragType={DND_TYPE_PROMAN_REPORT}
                item={createDragData()}
                onDragEnd={() => {}}
              >
                <div className={classes.draggableArea}>
                  <div className={classes.dragIconContainer}>
                    <FontAwesomeIcon icon={['fal', 'file']} />
                  </div>
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {t('projects:addIn.extensions.proman.dragToUpload')}
                  </span>
                </div>
              </DraggableElement>
            </>
          )}
        </div>
      )}

      <div className={classes.pdfViewer}>
        {isLoadingPdf ? (
          <div className={classes.loadingDocument}>
            <PrioSpinner size="default" />
          </div>
        ) : pdfBlobUrl ? (
          <>
            <object
              data={pdfBlobUrl}
              type="application/pdf"
              width="100%"
              height="100%"
            >
              {t('projects:addIn.extensions.proman.loadingPdf')}
            </object>
          </>
        ) : hasDocuments ? (
          <div className={classes.emptyState}>
            <Empty
              customMessage={t(
                'projects:addIn.extensions.proman.selectDocumentFirst'
              )}
            />
          </div>
        ) : (
          <div className={classes.emptyState}>
            <Empty
              customMessage={t('projects:addIn.extensions.proman.noPdfCreated')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PromanReportViewer;
